import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Send, RefreshCw, Copy, Check, AlertTriangle, Shield, CheckCircle2, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { policyAcknowledgmentService } from '@/services/policyAcknowledgmentService';
import { childConnectionService } from '@/services/childConnectionService';

interface PendingParentalApprovalProps {
  childUserId: string;
  childUsername: string;
  onRefreshStatus: () => void;
  isSubmitting?: boolean;
}

export const PendingParentalApproval: React.FC<PendingParentalApprovalProps> = ({
  childUserId,
  childUsername,
  onRefreshStatus,
  isSubmitting = false
}) => {
  const { toast } = useToast();
  const [connectionCode, setConnectionCode] = useState<string | null>(null);
  const [loadingCode, setLoadingCode] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    fetchConnectionCode();
    // Check if alert was previously sent in this session
    const alertSentKey = `parent_alert_sent_${childUserId}`;
    if (sessionStorage.getItem(alertSentKey) === 'true') {
      setAlertSent(true);
    }
  }, [childUserId]);

  const fetchConnectionCode = async () => {
    setLoadingCode(true);
    try {
      // Use getOrCreateChildCode which will auto-regenerate expired codes
      const code = await childConnectionService.getOrCreateChildCode(childUserId);
      setConnectionCode(code);
      
      if (!code) {
        console.error('Failed to get or create connection code');
      }
    } catch (err) {
      console.error('Error fetching connection code:', err);
    } finally {
      setLoadingCode(false);
    }
  };

  const handleCopyCode = async () => {
    if (connectionCode) {
      await navigator.clipboard.writeText(connectionCode);
      setCopied(true);
      toast({
        title: "Code Copied!",
        description: "Share this code with your parent.",
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendParentAlert = async () => {
    setSendingAlert(true);
    try {
      const result = await policyAcknowledgmentService.sendParentConsentAlert(childUserId, childUsername);
      
      if (result.success) {
        setAlertSent(true);
        // Persist alert sent state in session
        sessionStorage.setItem(`parent_alert_sent_${childUserId}`, 'true');
        
        toast({
          title: "Alert Sent Successfully!",
          description: "Your parent has been notified. They will see a notification on their dashboard. Please wait for them to complete the consent process.",
          duration: 6000,
        });
      } else {
        toast({
          title: "Could Not Send Alert",
          description: result.error || "Ask your parent to check their dashboard.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error sending parent alert:', error);
      toast({
        title: "Error",
        description: "Could not send alert. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingAlert(false);
    }
  };

  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    try {
      // Refresh the status through the parent component
      onRefreshStatus();
      
      // Give the parent component time to check
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // If we're still here after the refresh, show "still waiting" message
      toast({
        title: "Still Waiting",
        description: "Your parent hasn't completed the consent yet. Please ask them to check their notifications.",
        duration: 4000,
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="max-w-md w-full bg-card border-border">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 p-3 bg-amber-500/20 rounded-full w-fit">
            <Clock className="h-8 w-8 text-amber-500" />
          </div>
          <CardTitle className="text-xl text-foreground">
            Waiting for Parent Acknowledgment
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Status Message */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-foreground font-medium">
                  Sorry, your parent must acknowledge as you are under age before you can continue.
                </p>
              </div>
            </div>
          </div>

          {/* Connection Code Display */}
          <div className="bg-muted rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground mb-2">Your Connection Code</p>
            {loadingCode ? (
              <div className="animate-pulse bg-muted-foreground/20 h-10 rounded"></div>
            ) : connectionCode ? (
              <div className="flex items-center justify-center gap-2">
                <span className="text-2xl font-mono font-bold text-primary tracking-wider">
                  {connectionCode}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopyCode}
                  className="h-8 w-8"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ) : (
              <p className="text-muted-foreground">No code available</p>
            )}
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <h4 className="font-medium text-foreground text-sm">What happens next?</h4>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
              <li>Share your Connection Code with your parent</li>
              <li>Your parent enters the code on their dashboard</li>
              <li>They review and accept the policies on your behalf</li>
              <li>Once complete, you can access Players Lockeroom!</li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {alertSent ? (
              <div className="w-full bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-600">
                      Alert Sent Successfully!
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Awaiting parent response. They will see a notification on their dashboard.
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSendParentAlert}
                  disabled={sendingAlert || isSubmitting}
                  className="mt-3 w-full text-xs"
                >
                  {sendingAlert ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Resending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-3 w-3" />
                      Send Another Alert
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleSendParentAlert}
                disabled={sendingAlert || isSubmitting}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {sendingAlert ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Alert...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Parent Alert
                  </>
                )}
              </Button>
            )}

            <Button
              variant="outline"
              onClick={handleCheckStatus}
              disabled={isSubmitting || checkingStatus}
              className="w-full"
            >
              {checkingStatus ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Check Status
                </>
              )}
            </Button>
          </div>

          {/* Legal Notice */}
          <div className="pt-4 border-t border-border">
            <div className="flex gap-2 text-xs text-muted-foreground">
              <Shield className="h-4 w-4 flex-shrink-0" />
              <p>
                Under COPPA regulations, users under 13 require verified parental consent 
                before accessing online services.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingParentalApproval;
